<template>
  <div class="tenant-login-page">
    <aside class="brand-panel">
      <div class="brand-pattern" aria-hidden="true" />
      <div class="brand-inner">
        <div class="brand-mark">
          <img class="brand-logo" :src="defaultLogo" alt="" />
          <div class="brand-text">
            <span class="brand-name">{{ brandName }}</span>
            <span class="brand-tagline">{{ t('loginPage.tagline') }}</span>
          </div>
        </div>

        <h2 class="brand-headline">{{ t('loginPage.headline') }}<br />{{ t('loginPage.headline2') }}</h2>

        <ul class="brand-features">
          <li v-for="item in features" :key="item">{{ item }}</li>
        </ul>

        <p class="brand-footer">© {{ year }} {{ brandName }}</p>
      </div>
    </aside>

    <main class="auth-panel">
      <div class="auth-shell">
        <hc-login v-if="state.flng" @forget="getInfo" @forpsd="forpsd" />
        <hc-forget
          v-else
          :title="state.title"
          :btnType="state.btnType"
          @login="state.flng = true"
        />
      </div>
    </main>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue'
import hcLogin from './components/hc-login.vue'
import hcForget from './components/forget.vue'
import defaultLogo from '@/assets/logo.png'
import { DEFAULT_BRAND_NAME } from '@/utils/tenantBranding.js'
import { usePageI18n } from '@/composables/usePageI18n.js'

const brandName = DEFAULT_BRAND_NAME
const year = new Date().getFullYear()

const applyLabels = () => {}
const { t } = usePageI18n(applyLabels)

const features = computed(() => [
  t('loginPage.feature1'),
  t('loginPage.feature2'),
  t('loginPage.feature3'),
])

const state = reactive({
  flng: true,
  title: '',
  btnType: 0,
})

const getInfo = () => {
  state.flng = false
  state.title = t('loginPage.register')
  state.btnType = 0
}

const forpsd = () => {
  state.title = t('loginPage.forgotPwd')
  state.btnType = 1
  state.flng = false
}
</script>

<style lang="less" scoped>
.tenant-login-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  background: #f8fafc;
  overflow: hidden;
}

.brand-panel {
  position: relative;
  flex: 1;
  min-width: 0;
  display: none;
  color: #f8fafc;
  background: linear-gradient(160deg, #0c4a6e 0%, #0f172a 55%, #1e3a8a 100%);
  overflow: hidden;

  @media (min-width: 960px) {
    display: flex;
    align-items: center;
    flex: 0 0 50%;
    max-width: 50%;
  }
}

.brand-pattern {
  position: absolute;
  inset: 0;
  opacity: 0.28;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(56, 189, 248, 0.35) 0%, transparent 45%),
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: auto, 40px 40px, 40px 40px;
  pointer-events: none;
}

.brand-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  padding: 0 48px 0 56px;
}

.brand-mark {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 44px;
}

.brand-logo {
  width: 76px;
  height: 76px;
  border-radius: 16px;
  object-fit: cover;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  flex-shrink: 0;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.brand-name {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.25;
}

.brand-tagline {
  font-size: 17px;
  color: rgba(248, 250, 252, 0.82);
  line-height: 1.45;
  letter-spacing: 0.01em;
}

.brand-headline {
  margin: 0 0 28px;
  font-size: clamp(26px, 2.8vw, 32px);
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.brand-features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.brand-features li {
  position: relative;
  padding-left: 18px;
  font-size: 14px;
  color: rgba(248, 250, 252, 0.88);
  line-height: 1.5;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #7dd3fc;
  }
}

.brand-footer {
  margin: 48px 0 0;
  font-size: 12px;
  color: rgba(248, 250, 252, 0.4);
}

.auth-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  background: #f8fafc;

  @media (min-width: 960px) {
    flex: 1;
    max-width: none;
    padding: 32px 48px;
  }
}

.auth-shell {
  width: 100%;
  max-width: 400px;
}
</style>
